import {Fragment, useCallback, useEffect, useState} from 'react'
import CloseIcon from '@material-ui/icons/Close'
import AppTypography from 'src/components/elements/typography/AppTypography'
import {PropertyPhotoType} from 'src/types/property'
import {formattedPhoto} from 'src/utils/picture'
import {useImageSliderContext} from 'src/context/ImageSliderProvider'
import {useCurrentLocale} from 'src/hooks/locale'
import {getPropertyPhotoAltText} from 'src/utils/property'
import {PropertyType} from 'src/types/property'
import {getPropertyName} from 'src/hooks/property'
import FavoriteAndShareButtons from 'src/components/modules/property/singlePage/propertyHeader/FavoriteAndShareButtons'
import LargeSlider from 'src/components/modules/property/singlePage/propertyHeader/LargeSlider'
import AppPortal from 'src/components/helpers/AppPortal'
import CircularProgress from '@material-ui/core/CircularProgress'

function PropertyImagesGrid(props: {
  open: boolean
  property: PropertyType
  isFavorite: boolean
  onFavoriteClick: () => void
  handleClose: () => void
}) {
  const {open, isFavorite, onFavoriteClick, property, handleClose} = props
  const {hasMorePhotos, loading, loadAllPhotos} = useImageSliderContext()
  const [clickedIndex, setClickedIndex] = useState<number>(-1)
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const currentLocale = useCurrentLocale()
  const propertyName = getPropertyName(property, currentLocale, false)

  useEffect(() => {
    return () => {
      setClickedIndex(-1) //reset
    }
  }, [open])

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (clickedIndex !== -1) {
          setClickedIndex(-1)
          return
        }
        handleClose()
      }
    },
    [handleClose, clickedIndex],
  )

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onKeyDown])

  useEffect(() => {
    if (open && hasMorePhotos) {
      loadAllPhotos()
    }

    //wait for open to have make it's transition
    setTimeout(() => {
      setModalOpen(open)
    }, 300)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    document.body.style.overflowY = open
      ? 'hidden'
      : clickedIndex !== -1
      ? 'hidden'
      : 'initial'
  }, [clickedIndex, open])

  const onImageClick = (idx: number) => () => {
    setClickedIndex(idx)
  }

  return (
    <AppPortal>
      <div
        className={`fixed inset-0 z-modal transform duration-200 ${
          open ? 'translate-y-0 opacity-1' : 'opacity-0 translate-y-full'
        }`}
      >
        <div className="bg-white border-t border-gray-200 h-full flex flex-col justify-center items-center outline-none shadow-none">
          <div className="w-full flex flex-row justify-between items-center px-4 py-3 md:py-2 gap-2">
            <div className="w-full md:w-1/5" onClick={handleClose}>
              <button type="button" className="appearance-none block">
                <CloseIcon className="text-2xl pointer-events-none" />
              </button>
            </div>
            <div className="w-full md:w-3/5 flex flex-row justify-center items-center">
              <AppTypography
                variant="displayXSmallSlim"
                neutralColor={900}
                component="h3"
                className="text-center"
                onClick={handleClose}
              >
                {propertyName}
              </AppTypography>
            </div>
            <div className="w-full md:w-1/5 flex flex-row justify-end items-center">
              <div className="hidden md:block">
                <FavoriteAndShareButtons
                  isFavorite={isFavorite}
                  onFavoriteClick={onFavoriteClick}
                  property={property}
                />
              </div>
            </div>
          </div>

          {modalOpen && !loading ? (
            <ImagesGrid onImageClick={onImageClick} />
          ) : (
            <div className="py-6 w-full flex-1 flex flex-row justify-center items-center">
              <CircularProgress color="primary" size={20} />
            </div>
          )}
        </div>

        <LargeSlider
          open={clickedIndex !== -1}
          index={clickedIndex}
          handleClose={() => setClickedIndex(-1)}
        />
      </div>
    </AppPortal>
  )
}

function ImagesGrid(props: {onImageClick: (idx: number) => () => void}) {
  const {onImageClick} = props
  const currentLocale = useCurrentLocale()
  const {images} = useImageSliderContext()
  return (
    <Fragment>
      <div className="flex-1 w-full md:px-2 pb-12 overflow-y-auto">
        <div className="w-full sm:max-w-lg xl:max-w-xl mx-auto gap-1.5 grid grid-cols-2">
          {images.map((image: PropertyPhotoType, index: number) => {
            const newPhoto = formattedPhoto(image.image, 1024)
            const altText = getPropertyPhotoAltText(image, currentLocale)

            return (
              <div
                key={index}
                className={`block relative cursor-pointer group ${
                  index % 3 === 0 ? 'col-span-2' : 'col-span-1'
                }`}
                onClick={onImageClick(index)}
              >
                <img
                  className="w-full h-full block object-contain object-center hover:bg-black-50"
                  src={newPhoto.url}
                  alt={altText}
                  loading={index <= 4 ? 'eager' : 'lazy'}
                  //@ts-ignore
                  fetchPriority={index <= 3 ? 'high' : 'auto'}
                  style={{
                    filter: 'saturate(1.2) brightness(1.03) contrast(1.03)',
                  }}
                />
                <div className="bg-black/20 absolute inset-0 opacity-0 transition duration-150 group-hover:opacity-100"></div>
              </div>
            )
          })}
        </div>
      </div>
    </Fragment>
  )
}

export default PropertyImagesGrid
